import { posthogInstance } from '@/integrations/posthog'

import type { LogContext, LoggerProvider, LogLevel } from '../types'

export class PostHogLoggerProvider implements LoggerProvider {
  name = 'posthog'

  private tags: Record<string, string | number | boolean> = {}
  private contexts: Record<string, Record<string, unknown> | null> = {}

  log(
    level: LogLevel,
    message: string,
    context?: LogContext,
    error?: Error,
    category?: string,
  ): void {
    if (level === 'error' || level === 'fatal') {
      posthogInstance.captureException(error ?? message, {
        $exception_level: level,
        log_message: message,
        ...(category && { category }),
        ...context,
        ...this.tags,
      })
      return
    }

    posthogInstance.capture('log', {
      level,
      message,
      ...(category && { category }),
      ...(error && { error_name: error.name, error_message: error.message }),
      ...context,
      ...this.tags,
    })
  }

  setUser(
    _userId: string,
    email: string,
    name: string,
    userAccountId: string,
  ): void {
    // Do NOT call identify() here — analytics provider already handles it.
    // Calling identify() again with a different ID causes person merge issues.
    posthogInstance.setPersonProperties({
      email,
      name,
      userAccountId,
    })
  }

  clearUser(): void {
    // No-op — analytics provider reset() already handles this
  }

  setTag(key: string, value: string | number | boolean): void {
    this.tags[key] = value
    posthogInstance.register({ [key]: value })
  }

  setTags(tags: Record<string, string | number | boolean>): void {
    Object.assign(this.tags, tags)
    posthogInstance.register(tags)
  }

  setContext(key: string, context: Record<string, unknown> | null): void {
    this.contexts[key] = context
  }

  addBreadcrumb(
    _message: string,
    _category?: string,
    _level?: string,
    _data?: Record<string, unknown>,
  ): void {
    // PostHog does not have a native breadcrumb system
  }

  captureEvent(
    message: string,
    level: string = 'info',
    extras?: Record<string, unknown>,
  ): void {
    posthogInstance.capture(message, {
      level,
      ...extras,
    })
  }
}
